import { useTranslations } from "next-intl";
import Divider from "@/atoms/Divider";
import Icon from "@/atoms/Icon";
import type SafetyDepositPaymentType from "@/enums/SafetyDepositPaymentType";
import GroupPanel from "@/molecules/GroupPanel";
import type { DefaultComponentProps } from "@/types/component.types";
import convertToCurrency from "@/utils/convertToCurrency";
import AfterStayCosts from "./AfterStayCosts";
import styles from "./PriceDetails.module.css";
import type { ExtraCost } from "./PriceDetails.types";
import PriceRow from "./PriceRow";
import usePriceDetails from "./usePriceDetails";

export interface PriceDetailsProps extends DefaultComponentProps {
    readonly numberOfNights: number;
    readonly rentalPrice: number;
    readonly discountPrice?: number;
    readonly includedCosts?: string[];
    readonly excludedCosts?: ExtraCost[];
    readonly serviceFee?: number;
    readonly insuranceCosts?: number;
    readonly totalPrice: number;
    readonly afterStayCosts?: ExtraCost[];
    readonly depositPrice?: number;
    readonly depositType?: SafetyDepositPaymentType;
    readonly showPayUponArrivalAsterisk: boolean;
}

function PriceDetails(props: PriceDetailsProps) {
    const {
        afterStayCosts,
        depositPrice,
        depositType,
        discountPrice,
        excludedCosts,
        includedCosts,
        insuranceCosts,
        numberOfNights,
        rentalPrice,
        serviceFee,
        showPayUponArrivalAsterisk,
        testId = "price-details",
        totalPrice,
    } = props;
    const t = useTranslations();
    const { renderAsterisk, shouldShowAsteriskExplanation } = usePriceDetails({
        showPayUponArrivalAsterisk,
        excludedCosts,
        afterStayCosts,
        depositType,
    });

    return (
        <div data-testid={testId}>
            <PriceRow
                details={convertToCurrency({ cents: rentalPrice })}
                term={t("priceForNumNights.label", { numberOfNights })}
                testId={`${testId}-number-of-nights`}
            />

            {!!includedCosts?.length ? (
                <dl
                    className={styles.includedCosts}
                    data-testid={`${testId}-included-costs`}
                >
                    <dt data-testid={`${testId}-included-costs-term`}>
                        <Icon className="mr-1" name="check" />
                        {t("including.label")}:{" "}
                    </dt>
                    <dd
                        className={styles.includedCostsDetails}
                        data-testid={`${testId}-included-costs-details`}
                    >
                        {includedCosts?.join(", ")}
                    </dd>
                </dl>
            ) : null}

            {excludedCosts?.map((cost) => {
                const costTestId = cost.label.toLowerCase().replace(" ", "-");
                return (
                    <PriceRow
                        asterisk={renderAsterisk(cost.paymentType)}
                        className="mt-1-5"
                        details={convertToCurrency({ cents: cost.price })}
                        key={cost.label}
                        term={cost.label}
                        testId={`${testId}-excluded-cost-${costTestId}`}
                    />
                );
            })}

            {serviceFee ? (
                <PriceRow
                    className="mt-1-5"
                    details={convertToCurrency({ cents: serviceFee })}
                    term={t("serviceFee.label")}
                    testId={`${testId}-service-fee`}
                    tooltipContent={t("serviceFee.description")}
                    tooltipId={`${testId}-service-fee-tooltip`}
                />
            ) : null}

            {insuranceCosts ? (
                <PriceRow
                    className="mt-1-5"
                    details={convertToCurrency({ cents: insuranceCosts })}
                    term={t("cancellationInsurance.label")}
                    testId={`${testId}-insurance-costs`}
                />
            ) : null}

            {discountPrice ? (
                <>
                    <Divider borderColor="subtle" className="my-1-5" />
                    <PriceRow
                        className="mt-1-5"
                        details={`- ${convertToCurrency({ cents: discountPrice })}`}
                        term={t("discount.label")}
                        testId={`${testId}-discount-price`}
                        variant="discount"
                    />
                </>
            ) : null}

            <Divider borderColor="subtle" className="my-1-5" />

            <PriceRow
                className="mt-1-5"
                details={convertToCurrency({ cents: totalPrice })}
                term={t("totalPrice.label")}
                testId={`${testId}-total-price`}
                variant="bold"
            />
            {afterStayCosts?.length ? (
                <GroupPanel borderColor="subtle" className="mt-6">
                    <AfterStayCosts
                        afterStayCosts={afterStayCosts}
                        className={styles.afterStayCosts}
                        renderAsterisk={renderAsterisk}
                        term={t("afterStayCosts.title")}
                    />
                </GroupPanel>
            ) : null}

            {depositPrice && depositType ? (
                <GroupPanel borderColor="subtle" className="mt-4">
                    <p className={styles.depositDescription}>
                        {t("deposit.description", {
                            depositPrice: `${convertToCurrency({ cents: depositPrice })}${renderAsterisk(depositType)}`,
                        })}
                    </p>
                </GroupPanel>
            ) : null}

            {shouldShowAsteriskExplanation ? (
                <p className={styles.asteriskExplanation}>
                    *{t("payUponArrival.description")}
                </p>
            ) : null}
        </div>
    );
}

export default PriceDetails;
